import { ProgressCard } from "../common/ProgressCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CloudUpload,
  Droplets,
  FileText,
  Info,
  NotebookText,
  Package,
  SendHorizontal,
  TestTubeDiagonal,
} from "lucide-react";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { InfoCallout } from "../common/InfoCallout";
import { useWizard } from "@/contexts/WizardContext";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { LockedBanner } from "../common/LockedBanner";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";
import { cn } from "@/lib/utils";
import { SectionCard } from "../common/SectionCard";
import { SectionHeader } from "../common/SectionHeader";
import { WATER_FACTORS } from "@shared/factors/emission-factors";

const FIELD_LABEL_CLASS = "text-sm font-medium leading-5 text-gray-700";
const FIELD_INPUT_CLASS =
  "h-10 rounded-lg border-gray-300 bg-slate-50 px-3 text-base text-gray-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300";

function InfoIcon() {
  return <Info className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function UploadCloudIcon() {
  return <CloudUpload className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.8} />;
}

function WaterWithdrawalIcon() {
  return <TestTubeDiagonal className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function WaterConsumptionIcon() {
  return <Package className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function WaterSourceIcon() {
  return <Droplets className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function NotesIcon() {
  return <NotebookText className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function DocumentsIcon() {
  return <FileText className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.8} />;
}

function Scope3ArrowIcon() {
  return <SendHorizontal className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.8} />;
}

function Scope3InfoIcon() {
  return <Info className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.8} />;
}

function MetricField({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  required,
}: {
  label: string;
  value: number | null;
  onChange: (value: string) => void;
  placeholder: string;
  disabled: boolean;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className={FIELD_LABEL_CLASS}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      <Input
        type="number"
        step="0.1"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={FIELD_INPUT_CLASS}
      />
    </div>
  );
}

function ChoiceCard({
  selected,
  onClick,
  disabled,
  label,
  name,
  id,
}: {
  selected: boolean;
  onClick: () => void;
  disabled: boolean;
  label: string;
  name: string;
  id: string;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-[56px] flex-1 items-center gap-3 rounded-2xl border p-4 transition-colors",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        selected
          ? "border-primary/20 bg-primary/5"
          : "border-slate-200 bg-white hover:border-slate-300"
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        className="sr-only"
        checked={selected}
        onChange={onClick}
        disabled={disabled}
      />
      <span
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-slate-500 bg-primary" : "border-gray-500 bg-white"
        )}
        aria-hidden="true"
      >
        {selected && <span className="h-2 w-2 rounded-full bg-white" />}
      </span>
      <span className="text-sm font-medium leading-5 text-slate-900">{label}</span>
    </label>
  );
}

export function WaterCard({ onClose }: { onClose: () => void }) {
  const {
    waterData,
    updateWaterData,
    waterCompletion,
    forceSave,
    saveStatus,
    waterSupportingDocuments,
    setWaterSupportingDocuments,
    isLocked,
    isReadOnly,
  } = useWizard();
  const disabled = isLocked || isReadOnly;

  const handleNumberChange = (field: keyof typeof waterData, value: string) => {
    const numValue = value.trim() === "" ? null : parseFloat(value);
    updateWaterData({ [field]: isNaN(numValue as number) ? null : numValue });
  };

  const canShowScope3Metrics =
    waterData.totalWithdrawal !== null && waterData.annualConsumption !== null;
  const totalWithdrawal = waterData.totalWithdrawal ?? 0;
  const annualConsumption = waterData.annualConsumption ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Water (B6)"}
        description={"Tell us how much water your business uses. Start with your water bills or withdrawal logs"}
        onClose={onClose}
        onSave={forceSave}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Environment"}
      />


      {disabled && <LockedBanner />}

      <SectionCard header={<SectionHeader icon={<WaterWithdrawalIcon />} title={"1) Water Withdrawal"} />}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <MetricField
            label={"Total water withdrawal (m³)"}
            value={waterData.totalWithdrawal}
            onChange={(value) => handleNumberChange("totalWithdrawal", value)}
            placeholder={"e.g. 15000"}
            disabled={disabled}
          />
          <MetricField
            label={"Withdrawal in high water-stress areas (m³)"}
            value={waterData.withdrawalInStressAreas}
            onChange={(value) => handleNumberChange("withdrawalInStressAreas", value)}
            placeholder={"e.g. 5000"}
            disabled={disabled}
          />
        </div>
        <InfoCallout label={`${"Tip:"}`} text={"Check your water bills or withdrawal logs for accurate figures"} />
      </SectionCard>

      <SectionCard header={<SectionHeader icon={<WaterConsumptionIcon />} title={"2) Water Consumption"} />}>
        <Label className={FIELD_LABEL_CLASS}>{"Do you have significant water-consuming processes? (optional)"}</Label>

        <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
          <ChoiceCard
            id="water_yes"
            name="water_consumption"
            selected={waterData.hasConsumptionProcess === true}
            onClick={() => updateWaterData({ hasConsumptionProcess: true })}
            disabled={disabled}
            label={"Yes"}
          />
          <ChoiceCard
            id="water_no"
            name="water_consumption"
            selected={waterData.hasConsumptionProcess === false}
            onClick={() => updateWaterData({ hasConsumptionProcess: false })}
            disabled={disabled}
            label={"No"}
          />
        </div>
        <div className="max-w-sm pt-2">
          <MetricField
            label={"Annual water consumption (m³)"}
            value={waterData.annualConsumption}
            onChange={(value) => handleNumberChange("annualConsumption", value)}
            placeholder={"e.g. 8000"}
            disabled={disabled}
            required
          />
        </div>
        <InfoCallout label={`${"Note:"}`} text={"Water consumption is the amount your business actually uses — calculated as total water taken in minus any returned to rivers, drains, or ground"} />
      </SectionCard>

      <SectionCard header={<SectionHeader icon={<WaterSourceIcon />} title={"3) Water Source (Optional)"} />}>
        <div className="max-w-md">
          <div className="flex flex-col gap-1">
            <Label className={FIELD_LABEL_CLASS}>{"Main water source"}</Label>
            <Input
              placeholder={"e.g. Municipal supply, Groundwater, Surface water"}
              value={waterData.waterSource}
              onChange={(e) => updateWaterData({ waterSource: e.target.value })}
              disabled={disabled}
              className={FIELD_INPUT_CLASS}
            />
          </div>
        </div>
      </SectionCard>

      <section className="flex flex-col gap-6 rounded-xl border-2 border-gray-200 bg-primary/10 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-[5px] bg-primary px-4 py-1 text-sm font-bold uppercase tracking-wide text-white">
            Scope 3
          </span>
          <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">{"Water Supply & Treatment Emissions"}</h3>
        </div>
        <p className="text-base leading-6 text-slate-500">{"Emissions from water supply and wastewater treatment (Scope 3)"}</p>

        {canShowScope3Metrics ? (
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-medium leading-4 text-gray-500">{"Water Supply Emissions"}</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-xl font-bold leading-7 text-gray-900">
                    {((totalWithdrawal * WATER_FACTORS.supply_kgco2e_per_cubic_meter) / 1000).toFixed(3)}
                  </span>
                  <span className="text-sm leading-5 text-gray-500">tCO2e</span>
                </div>
              </div>
              <p className="text-sm leading-5 text-gray-500">
                ({totalWithdrawal.toFixed(1)} m³ × {WATER_FACTORS.supply_kgco2e_per_cubic_meter} kg/m³)
              </p>
              <div className="opacity-20">
                <Scope3InfoIcon />
              </div>
            </div>

            <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-medium leading-4 text-gray-500">{"Wastewater Treatment"}</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-xl font-bold leading-7 text-gray-900">
                    {((annualConsumption * WATER_FACTORS.treatment_kgco2e_per_cubic_meter) / 1000).toFixed(3)}
                  </span>
                  <span className="text-sm leading-5 text-gray-500">tCO2e</span>
                </div>
              </div>
              <p className="text-sm leading-5 text-gray-500">
                ({annualConsumption.toFixed(1)} m³ × {WATER_FACTORS.treatment_kgco2e_per_cubic_meter} kg/m³)
              </p>
              <div className="opacity-20">
                <Scope3ArrowIcon />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            {"Enter water withdrawal and consumption above to see Scope 3 emissions."}
          </div>
        )}

        <div className="flex items-start gap-2 rounded-lg border border-slate-500 bg-white p-3">
          <InfoIcon />
          <p className="text-xs leading-4 text-slate-500">
            <span className="font-bold">{"Note:"}</span> {"Emission factors based on UK Government GHG Conversion Factors 2024. These are indirect emissions from your water usage."}
          </p>
        </div>
      </section>

      <SectionCard header={<SectionHeader icon={<NotesIcon />} title={"4) Additional Notes (Optional)"} />}>
        <Textarea
            placeholder={"e.g. Water recycling systems, rainwater harvesting, conservation measures..."}
            className="min-h-[136px] rounded-lg border-gray-300 bg-slate-50 px-3 py-2 text-base leading-6 text-gray-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300"
            value={waterData.notes}
            onChange={(e) => updateWaterData({ notes: e.target.value })}
            disabled={disabled}
          />
      </SectionCard>

      <SectionCard header={<SectionHeader icon={<DocumentsIcon />} title={"Supporting Documents"} />}>
        <DocumentUploadSection
          fieldIdentifier="environment-water"
          documents={waterSupportingDocuments}
          onDocumentsChange={setWaterSupportingDocuments}
          currentTab="environment"
          label={"Supporting Documents"}
          description={"Upload water bills, withdrawal logs, or metering records"}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
          maxFiles={10}
          borderColor="border-slate-200"
          hideHeader
          className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10"
          emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          documentsListClassName="space-y-3"
          itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
          actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          emptyStateContent={
            <div className="flex flex-col items-center text-center">
              <div className="pb-4">
                <div className="rounded-full bg-slate-500/5 p-4">
                  <UploadCloudIcon />
                </div>
              </div>
              <p className="text-base font-bold leading-6 text-slate-800">
                Click to upload or drag and drop
              </p>
              <p className="pt-1 text-sm leading-5 text-slate-500">
                {"Upload water bills, withdrawal logs, or metering records"}
              </p>
            </div>
          }
        />
      </SectionCard>

      <TransitionMeasuresBlock topicId="water" currentTab="environment" disabled={disabled} />

      <ProgressCard completion={waterCompletion} />
    </div>
  );
}
